/**
 * Phase 3 chunk 3: registry of marquee satellites that get a 3D
 * model when the camera is close enough.  All other satellites
 * remain dots.
 *
 * The registry is intentionally sparse.  Adding a satellite here
 * does NOT add it to the catalog — it just upgrades the visual
 * representation when the user zooms in to inspect it.
 *
 * `nameMatcher` is used for constellation-style entries (Starlink:
 * thousands of NORADs, all visually identical).  Either `norad` or
 * `nameMatcher` must be set; both is allowed (norad takes priority
 * for lookup speed).
 */

import * as Cesium from 'cesium';

export type ShapeKind = 'box' | 'cylinder' | 'panel';

export interface MarqueeSpec {
  /** Display name used in tooltips / debug logs.  Not used for matching. */
  label: string;
  /** Shape primitive used at LOD threshold.  See MarqueeShapeLayer for the geometry. */
  shape: ShapeKind;
  /** Physical box dimensions in meters (x, y, z).  Real-world ISS is ~108m × 73m × 20m. */
  dimensionsMeters: { x: number; y: number; z: number };
  /** How much to multiply the physical size before rendering — without this, even ISS is invisible. */
  visualScale: number;
  /** Albedo color for the rendered primitive. */
  color: Cesium.Color;
  /** Optional exact NORAD match. */
  norad?: number;
  /** Optional name-prefix matcher (case-sensitive) for constellation entries. */
  namePrefix?: string;
  /**
   * Phase 4 chunk 8A — when set, MarqueeShapeLayer loads this glTF via
   * `Cesium.Model.fromGltfAsync()` instead of building a procedural
   * primitive.  Path is relative to the web root, e.g. `/models/iss.glb`.
   * Files belong in `public/models/`; see `public/models/CREDITS.md`
   * for the licensing checklist any new model must clear.
   *
   * Left undefined → falls back to the chunk-3A procedural primitive
   * (Box / Cylinder / Panel by `shape`).  This is deliberate: shipping
   * the swap-in *infrastructure* without paying the asset-acquisition
   * tax means contributors can drop in real models per satellite as
   * they get sourced, without touching MarqueeShapeLayer.
   */
  gltfUri?: string;
}

const C = (css: string) => Cesium.Color.fromCssColorString(css);

/**
 * Marquee roster locked in `docs/phase3.md` § II row 8.
 *
 * Visual scales are deliberately exaggerated so the model is
 * recognizable at typical zoom-to-inspect distances (~5000km
 * camera-to-satellite). Real-scale ISS at 5000km would be ~0.05
 * pixels — useless. ×100 puts it around 5km wide on screen.
 */
export const MARQUEE_SPECS: ReadonlyArray<MarqueeSpec> = [
  // Crewed stations
  {
    label: 'ISS (ZARYA)',
    norad: 25544,
    shape: 'panel',                // wide flat box approximating the truss + arrays (fallback when glTF absent)
    dimensionsMeters: { x: 108, y: 73, z: 20 },
    visualScale: 120,              // applied to both glTF (Cesium.Model.scale) and procedural primitive — keeps on-screen size consistent
    color: C('#e8e8e8'),
    // Phase 5 chunk 7 — real NASA Solar System Exploration glTF.
    // Run `make fetch-models` to populate; falls back to procedural panel
    // automatically when the file is absent. See public/models/CREDITS.md.
    gltfUri: '/models/iss.glb',
  },
  {
    label: 'Tiangong (CSS)',
    norad: 48274,
    shape: 'panel',
    dimensionsMeters: { x: 55, y: 39, z: 16 },
    visualScale: 150,
    color: C('#ffd28a'),
    // Phase 9 chunk 5 — community CC0 / open-license glTF target.
    // See public/models/CREDITS.md "Tiangong" entry for the sourcing
    // checklist + a curated set of candidate sources. Falls back to
    // the procedural panel until `make fetch-models` populates the
    // binary (slot reserved per § II row 7 + § II row 8 acceptance).
    gltfUri: '/models/tiangong.glb',
  },

  // Telescope
  {
    label: 'Hubble',
    norad: 20580,
    shape: 'cylinder',
    dimensionsMeters: { x: 14, y: 14, z: 4 },     // diameter, diameter, length-ish (Cesium box stand-in)
    visualScale: 250,
    color: C('#c0c0d8'),
    // Phase 9 chunk 5 — NASA 3D Resources glTF target (Hubble Space
    // Telescope, public-domain under NASA image-use guidelines). See
    // public/models/CREDITS.md "Hubble" for sourcing details. Falls
    // back to the procedural cylinder until the binary is fetched.
    gltfUri: '/models/hubble.glb',
  },
  // James Webb Space Telescope — at Sun-Earth L2, ~1.5 Mkm out, so
  // the camera will never come within the chunk-3A 5,000 km
  // marquee-shape render gate; this spec exists so JWST shows up
  // in the sky-view pass-arc roster (post-Phase-11). The shape +
  // scale + glTF fields are harmless never-rendered slots.
  {
    label: 'JWST',
    norad: 50463,
    shape: 'panel',
    dimensionsMeters: { x: 21, y: 14, z: 4 },     // sunshield extent
    visualScale: 300,
    color: C('#d0c8e0'),
    // No glTF — keeping the licensing-clean glTF roster (Phase 9
    // chunk 5) limited to the four already-sourced models. JWST at
    // L2 isn't selectable for marquee-shape rendering anyway.
  },

  // Cargo capsules — small, need extra visual scale.
  // NORAD IDs change per launch; we match the most-recent attached
  // visit. Phase 5 cleanup: refresh these from LL2 ingest.
  {
    label: 'Dragon (CRS-30)',
    norad: 59365,
    shape: 'box',
    dimensionsMeters: { x: 4, y: 4, z: 8 },
    visualScale: 600,
    color: C('#f0f0f0'),
    // fallback per Phase 9 § II decision 9 — licensing-clean glTF not
    // yet sourced (SpaceX / commercial cargo capsule models are mostly
    // CC-BY-NC on Sketchfab; chunk 5 acceptance was 4 of 7, ISS +
    // Hubble + Tiangong + Soyuz, so Dragon stays procedural).
  },
  {
    label: 'Cygnus NG-21',
    norad: 60562,
    shape: 'cylinder',
    dimensionsMeters: { x: 3, y: 3, z: 6 },
    visualScale: 700,
    color: C('#e0e0c0'),
    // fallback per Phase 9 § II decision 9 — licensing-clean glTF not
    // yet sourced. Northrop Grumman commercial-cargo asset is similarly
    // CC-BY-NC across the Sketchfab landscape; revisit if a CC0 source
    // appears.
  },
  {
    label: 'Soyuz MS-26',
    norad: 60959,
    shape: 'box',
    dimensionsMeters: { x: 3, y: 3, z: 7 },
    visualScale: 700,
    color: C('#88ccaa'),
    // Phase 9 chunk 5 — community CC0 glTF target per § II row 7. See
    // public/models/CREDITS.md "Soyuz" entry. Falls back to the
    // procedural box until the binary is fetched.
    gltfUri: '/models/soyuz.glb',
  },

  // Constellation: a single panel-shaped stand-in reused for every
  // STARLINK-prefixed satellite.  ~7000 sats — only renders for the
  // selected one.
  {
    label: 'Starlink (generic)',
    namePrefix: 'STARLINK',
    shape: 'panel',
    dimensionsMeters: { x: 3.7, y: 1.6, z: 0.3 },
    visualScale: 900,
    color: C('#88aacc'),
    // fallback per Phase 9 § II decision 9 — licensing-clean glTF not
    // yet sourced. The chunk-5 acceptance floor (4 of 7) deliberately
    // skipped Starlink per § II row 7's "skip Dragon/Cygnus/Starlink
    // if licensing is unclear" guidance.
  },
];

/**
 * Resolve a NORAD + name to a marquee spec, or null if the satellite
 * isn't in the roster.
 */
export function findMarqueeSpec(norad: number, name: string | null = null): MarqueeSpec | null {
  for (const spec of MARQUEE_SPECS) {
    if (spec.norad === norad) return spec;
  }
  if (name !== null) {
    for (const spec of MARQUEE_SPECS) {
      if (spec.namePrefix && name.startsWith(spec.namePrefix)) return spec;
    }
  }
  return null;
}
